///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone } from '@angular/core';
import { GtthPosition } from '../entities/position';
import { GtthLinkInfoComponent } from './link-info/link-info.component';
import { GtthUnitInfoComponent } from './unit-info/unit-info.component';
import { GtthSectorInfoComponent } from './sector-info/sector-info.component';
import { FeatureTypeEnum, GtthStepGroupEnum } from '../../../generated/shared-types.model';
import { GtthHistoryService } from '../engine/history-service';
import { NgElement, WithProperties } from '@angular/elements';
import { InfoWindowsCustomElementRegisterService } from './info-windows-custom-element-register.service';
import { GtthSelectedAreaInfoComponent } from './select-area/selected-area-info.component';
import { GtthInfoOverlayComponent } from './gtth-info-overlay-component';
import { GtthEntityInfoWindowContent } from './gtth-entity-info-window-content-interface';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable({
    providedIn: 'root'
})
export class GtthInfoWindowsService {
    private readonly overlayBuilder: GtthInfoOverlayComponent;
    private activeInfoWindow: GtthEntityInfoWindowContent;
    private infoWindow: any;
    private closeListener: any;
    private entityId: number;

    constructor(
        private readonly ngZone: NgZone,
        private readonly infoWindowsCustomElementRegisterService: InfoWindowsCustomElementRegisterService
    ) {
        this.overlayBuilder = new GtthInfoOverlayComponent();
        this.infoWindowsCustomElementRegisterService.registerInfoWindowComponents();
    }

    private _historyService: GtthHistoryService;

    set historyService(value: GtthHistoryService) {
        this._historyService = value;
    }

    get isWindowOpen(): boolean {
        return !!this.activeInfoWindow;
    }

    openSelectAreaStationInfoWindow(position: GtthPosition, polygonIndex?: number): Promise<any> {
        const param = isDefined(polygonIndex) ? polygonIndex : undefined;
        return this.openInfoWindow(param, position, FeatureTypeEnum.SELECTED_AREA);
    }

    openUnitInfoWindow(id: number, position: GtthPosition): Promise<any> {
        if (
            this._historyService.logicalTopology.getSelectedStationsCount() > 1 &&
            this._historyService.logicalTopology.getStation(id).isSelected()
        ) {
            if (
                this._historyService.selectedStepNumber !== GtthStepGroupEnum.LOCATIONS_AND_LOSES &&
                !this._historyService.logicalTopology.hasSelectedLinks()
            ) {
                return this.openInfoWindow(id, position, FeatureTypeEnum.UNIT_GENERAL);
            } else {
                return this.openInfoWindow(undefined, position, FeatureTypeEnum.SELECTED_AREA);
            }
        } else {
            this.entityId = id;
            return this.openInfoWindow(id, position, FeatureTypeEnum.UNIT_GENERAL);
        }
    }

    openLinkInfoWindow(id: number, position: GtthPosition): Promise<any> {
        if (
            this._historyService.logicalTopology.getSelectedLinksCount() > 1 &&
            this._historyService.logicalTopology.getLink(id).isSelected()
        ) {
            return this.openInfoWindow(undefined, position, FeatureTypeEnum.SELECTED_AREA);
        } else {
            this.entityId = id;
            return this.openInfoWindow(id, position, FeatureTypeEnum.LINK);
        }
    }

    openSectorInfoWindow(sectorId: number, position: GtthPosition): Promise<any> {
        return this.openInfoWindow(sectorId, position, FeatureTypeEnum.SECTOR);
    }

    openInfoWindow(entityId: number, position: GtthPosition, featureType: FeatureTypeEnum, storeInSession = true): Promise<any> {
        if (storeInSession) {
            this._historyService.projectUserSessionService.saveInfoWindowConfig(entityId, position, featureType);
        }

        return new Promise((resolve) => {
            const newEntityId = this.entityId;

            if (this.infoWindow && !this.closeInfoWindow()) {
                resolve(undefined);
                return;
            }

            this.entityId = newEntityId;

            const positionArray = [position];
            if (featureType === FeatureTypeEnum.LINK) {
                const link = this._historyService.logicalTopology.getLink(entityId);
                if (!link) {
                    resolve(undefined);
                    return;
                }
                positionArray.push(link.getFromStation().getPosition());
                positionArray.push(link.getToStation().getPosition());
            }

            const componentElement = this.createInfoWindowContent(entityId, featureType);
            this.infoWindow = this.overlayBuilder.createOverlay(positionArray, componentElement, this._historyService);
            resolve(this.infoWindow);

            this.closeListener = this.infoWindow.addListener('closeclick', () => {
                this.ngZone.run(() => {
                    this.closeInfoWindow();
                });
            });
        });
    }

    registerActiveInfoWindow(activeInfoWindow: GtthEntityInfoWindowContent): void {
        this.activeInfoWindow = activeInfoWindow;
    }

    closeInfoWindow(): boolean {
        if (this.activeInfoWindow && !this.activeInfoWindow.isValid()) {
            return false;
        }

        this._historyService.projectUserSessionService.clearInfoWindow();

        this.entityId = undefined;
        this.activeInfoWindow = undefined;
        if (this.infoWindow) {
            if (this.closeListener) {
                google.maps.event.removeListener(this.closeListener);
            }
            this.infoWindow.setMap(null);
            this.infoWindow = undefined;
        }

        return true;
    }

    forceInfoWindowClose(): boolean {
        if (this.activeInfoWindow) {
            this.activeInfoWindow.forceClose = true;

            this.entityId = undefined;
            this.activeInfoWindow = undefined;
            if (this.infoWindow) {
                if (this.closeListener) {
                    google.maps.event.removeListener(this.closeListener);
                }
                this.infoWindow.setMap(null);
                this.infoWindow = undefined;
            }

            return true;
        } else {
            return false;
        }
    }

    getInfoWindowEntityId(): number {
        return this.entityId;
    }

    private createInfoWindowContent(entityId: number, featureType: FeatureTypeEnum): any {
        let componentElement: NgElement & WithProperties<GtthEntityInfoWindowContent>;

        switch (featureType) {
            case FeatureTypeEnum.UNIT_GENERAL:
                componentElement = document.createElement(GtthUnitInfoComponent.CUSTOM_ELEMENT_TAG_NAME) as any;
                break;
            case FeatureTypeEnum.LINK:
                componentElement = document.createElement(GtthLinkInfoComponent.CUSTOM_ELEMENT_TAG_NAME) as any;
                break;
            case FeatureTypeEnum.SELECTED_AREA:
                componentElement = document.createElement(GtthSelectedAreaInfoComponent.CUSTOM_ELEMENT_TAG_NAME) as any;
                break;
            case FeatureTypeEnum.SECTOR:
                componentElement = document.createElement(GtthSectorInfoComponent.CUSTOM_ELEMENT_TAG_NAME) as any;
                break;
        }

        componentElement.entityId = entityId;
        componentElement.forceClose = false;
        componentElement.addEventListener('closeCallback', () => this.closeInfoWindow());

        return componentElement;
    }
}
